import { cloneElement } from 'react'
import * as m from 'motion/react-m'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { cls } from './cls'
import { Ico } from './Ico'
import { Button } from '@/ui'

interface ShellProps {
  onClose: () => void
  /** Fires for the Escape key specifically, before onClose — Radix composes
   *  this ahead of its own dismiss, so a caller that needs to tell "closed
   *  via Escape" apart from "closed via Cancel/X" (both of which only ever
   *  reach onClose) can tag the former here. Optional: most callers don't
   *  care how the dialog closed and can rely on onClose alone. */
  onEscapeKeyDown?: () => void
  narrow?: boolean
  children: React.ReactNode
  className?: string
}

// Backdrop + centered card, on Radix Dialog: role=dialog, focus trap and
// focus return come from the primitive. `open` is always true because a
// caller only ever mounts a ModalShell while its sheet is open (see e.g.
// `{outcome && <OutcomeSheet .../>}` in App.tsx) and unmounts it to close —
// AnimatePresence at that call site is what plays the exit transition below,
// same as before this was Radix-backed.
//
// onPointerDownOutside is suppressed: a backdrop click used to call onClose
// directly, silently discarding whatever the BDR had typed into the sheet.
// Only Escape and an explicit Cancel/X now close it.
export function ModalShell({ onClose, onEscapeKeyDown, narrow, children, className }: ShellProps) {
  return (
    <DialogPrimitive.Root open onOpenChange={open => { if (!open) onClose() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay asChild>
          <m.div
            className="fixed inset-0 z-[80] bg-ink-1000/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          />
        </DialogPrimitive.Overlay>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onPointerDownOutside={e => e.preventDefault()}
          onEscapeKeyDown={onEscapeKeyDown}
          asChild
        >
          <m.div
            className={cls(
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[80]',
              'bg-white border border-ink-200 border-t-[3px] border-t-brand outline-none',
              'flex flex-col max-h-[calc(100vh-32px)] overflow-auto',
              narrow ? 'w-[min(440px,calc(100vw-32px))]' : 'w-[min(560px,calc(100vw-32px))]',
              className,
            )}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.99 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {children}
          </m.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

interface HeadProps {
  eyebrow?: string
  title: React.ReactNode
  onClose: () => void
}

export function ModalHead({ eyebrow, title, onClose }: HeadProps) {
  const xIcon = cloneElement(Ico.x, { width: 12, height: 12 })
  return (
    <header className="flex items-start justify-between gap-3 px-[22px] pt-[18px] pb-2 border-b border-ink-100">
      <div>
        {eyebrow && (
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-ink-500 mb-1">
            {eyebrow}
          </span>
        )}
        <DialogPrimitive.Title asChild>
          <h3 className="font-display text-[20px] font-medium tracking-tight text-ink-1000 m-0">
            {title}
          </h3>
        </DialogPrimitive.Title>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Stäng dialogruta"
        title="Stäng dialogruta"
        className="inline-flex items-center justify-center w-7 h-7 p-0 rounded-[2px] bg-transparent border border-ink-200 text-ink-500 cursor-pointer transition-colors hover:text-ink-1000 hover:border-ink-1000"
      >
        {xIcon}
      </button>
    </header>
  )
}

export function ModalSection({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={cls('px-[22px] pt-3.5 pb-1 last-of-type:pb-[18px]', className)}>
      {children}
    </section>
  )
}

export function ModalLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] font-semibold uppercase tracking-widest text-ink-500 mb-2">
      {children}
    </label>
  )
}

interface FootProps {
  onCancel: () => void
  onSave: () => void
  saveDisabled?: boolean
  saveLabel?: React.ReactNode
  cancelLabel?: string
}

export function ModalFoot({
  onCancel, onSave, saveDisabled = false, saveLabel = 'Spara', cancelLabel = 'Avbryt',
}: FootProps) {
  return (
    <footer className="flex items-center justify-end gap-2 px-[22px] py-3.5 border-t border-ink-100 mt-auto">
      <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
      <Button variant="primary" disabled={saveDisabled} onClick={onSave}>
        {saveLabel}
      </Button>
    </footer>
  )
}
